import { useEffect, useRef, useState } from 'react';
import { IBaseResponse } from '@nkr/models/index';
import { IStrongBoxCenterFilter } from '@nkr/models/strong-box/strong-box-filter';
import { IStrongBoxCenterPhysicalInventoryContract, IStrongBoxCenterPhysicalInventoryListing } from '@nkr/models/strong-box';
import { AuthActionCreators } from '@nkr/store/authorization';
import { useAppHookContext } from '@nkr/utils/rest-api';

interface IRetVal {
    list: IStrongBoxCenterPhysicalInventoryListing[],
    loading: boolean,
    error: string,
    record: (centerId: number, count: number, initials: string) => void,
    update: (id: number, count: number) => void
}

const useStrongBoxCenterPhysicalInventory = (onRecorded?: (physicalInventory: IStrongBoxCenterPhysicalInventoryContract) => void, filter?: IStrongBoxCenterFilter): IRetVal => {
    const { dispatch } = useAppHookContext();

    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [list, setList] = useState<IStrongBoxCenterPhysicalInventoryListing[]>([]);

    const listRef = useRef<IStrongBoxCenterPhysicalInventoryListing[]>([]);

    useEffect(() => {
        if (filter) {
            setLoading(true);
            setError('');

            const fetchData = async () => {
                let config = {
                    url: `${__API__}/strongbox/physical-inventory/search`,
                    data: filter
                };

                try {
                    const response: IBaseResponse = await dispatch(AuthActionCreators.apiPost(config));

                    if (response && response.data.results && response.data.success) {
                        setList(response.data.results);

                        listRef.current = response.data.results;
                    } else {
                        setError('Unable to load data');
                        setList([]);

                        listRef.current = [];
                    }
                } catch (err) {
                    setList([]);
                    setError(err);
                } finally {
                    setLoading(false);
                }
            }

            fetchData();
        }
    }, [filter]);

    const record = async (centerId: number, count: number, initials: string) => {
        setError('');
        setLoading(true);

        let options = {
            url: `${__API__}/strongbox/physical-inventory`,
            data: {
                centerId,
                count,
                initials
            }
        };

        try {
            const r: IBaseResponse = await dispatch(AuthActionCreators.apiPost(options));

            if (r.data.success) {
                onRecorded(r.data.results);
            } else {
                setError('An error occurred while recording physical inventory.');
            }
        } catch (err) {
            setError(`An error occurred while recording physical inventory: ${err}`);
        } finally {
            setLoading(false);
        }
    };

    const update = async (id: number, count: number) => {
        setError('');
        setLoading(true);

        let options = {
            url: `${__API__}/strongbox/physical-inventory`,
            data: {
                id,
                count
            }
        };

        try {
            const r: IBaseResponse = await dispatch(AuthActionCreators.apiPut(options));

            if (r.data.success) {
                let updatedList = listRef.current.map(x=> {
                    if (x.id == id) {
                        return r.data.results;
                    }

                    return x;
                });

                setList(updatedList);

                listRef.current = updatedList;

                onRecorded(r.data.results);
            } else {
                setError('An error occurred while updating physical inventory.');
            }
        } catch (err) {
            setError(`An error occurred while updating physical inventory: ${err}`);
        } finally {
            setLoading(false);
        }
    };

    return { list, loading, error, record, update }
}

export default useStrongBoxCenterPhysicalInventory;